import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import Glossy3DCard from "@/components/Glossy3DCard";
import { PageHeader } from "@/components/Common";
import {
  LayoutDashboard, Server, Users, DollarSign, Activity, CheckCircle2, Circle, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const stat = (label, value, icon, tint) => ({ label, value, icon, tint });

export default function Dashboard() {
  const { domains } = useDomains();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/overview").then((r) => setData(r.data)).catch(() => {});
  }, [domains.length]);

  const stats = [
    stat("Workspace Domains", data?.domain_count ?? 0, Server, "text-emerald-600"),
    stat("Connected", data?.connected_count ?? 0, CheckCircle2, "text-teal-600"),
    stat("Total Users", data?.total_users ?? 0, Users, "text-cyan-600"),
    stat("Est. Monthly", `$${(data?.monthly_estimate ?? 0).toLocaleString()}`, DollarSign, "text-amber-600"),
  ];

  return (
    <div className="animate-fade-up">
      <PageHeader title="Command Dashboard" subtitle="Everything across your Google Workspace estate, in one glance" icon={LayoutDashboard} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s, i) => (
          <Glossy3DCard key={s.label} testid={`stat-${i}`} className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <s.icon className={`h-6 w-6 ${s.tint}`} />
              <ArrowUpRight className="h-4 w-4 text-slate-300" />
            </div>
            <p className="font-heading text-3xl font-bold text-slate-900">{s.value}</p>
            <p className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">{s.label}</p>
          </Glossy3DCard>
        ))}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Glossy3DCard tilt={false} className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-slate-800">Connected Workspaces</h3>
              <Link to="/accounts" className="text-sm font-medium text-emerald-600 hover:underline">Manage →</Link>
            </div>
            {(!data || data.domains.length === 0) ? (
              <p className="rounded-2xl bg-white/50 p-8 text-center text-sm text-slate-500">No domains connected yet.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {data.domains.map((d) => (
                  <div key={d.id} className="rounded-2xl bg-white/60 p-4" data-testid={`dash-domain-${d.domain}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-heading font-semibold text-slate-800">{d.label}</p>
                      <span className={`flex items-center gap-1 text-xs font-medium ${d.status === "connected" ? "text-emerald-600" : d.status === "error" ? "text-rose-500" : "text-slate-400"}`}>
                        {d.status === "connected" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                        {d.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{d.domain}</p>
                    <p className="mt-2 text-sm text-slate-600">{d.user_count ?? "—"} users</p>
                  </div>
                ))}
              </div>
            )}
          </Glossy3DCard>
        </div>

        <Glossy3DCard tilt={false} className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-600" />
            <h3 className="font-heading text-lg font-semibold text-slate-800">Recent Activity</h3>
          </div>
          <div className="space-y-3">
            {(!data || data.recent_activity.length === 0) ? (
              <p className="text-sm text-slate-500">No activity yet.</p>
            ) : data.recent_activity.map((a) => (
              <div key={a.id} className="border-l-2 border-emerald-200 pl-3">
                <p className="text-sm font-medium text-slate-700">{a.operation.replace(/_/g, " ")}</p>
                <p className="text-xs text-slate-400">{a.actor} · {new Date(a.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </Glossy3DCard>
      </div>
    </div>
  );
}
